import type { CSSProperties } from 'react';
import { useSuggestions } from '../../hooks/useOsint';
import { SuggestionCard } from './OverviewSections';

/**
 * RailSuggestionsFooter — FR-W-1 (cycle-105.8).
 *
 * The Suggestions widget rendered as the InvestigationRail footer. Reuses
 * `SuggestionCard` + `useSuggestions` (NFR-3 reuse-over-rebuild) — the suggestion
 * feed was otherwise orphaned in the V2 workspace (no consumers). Read-only
 * ambient paradox / forge / cluster candidates: no Promote button is rendered
 * (NFR-4 no dead buttons), since promotion lives in the dedicated spawn flow
 * (`investigationSpawnResolver`). Hidden by the rail when collapsed (48px).
 */
export function RailSuggestionsFooter() {
  const { data, isError, isLoading } = useSuggestions(6);
  const suggestions = data?.suggestions ?? [];

  const mutedStyle: CSSProperties = {
    padding: '10px 14px',
    fontFamily: 'var(--cd-font-mono)',
    fontSize: 10,
    color: 'var(--cd-fg-muted)',
    letterSpacing: '0.04em',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Sectional header — mirrors the canonical rail footer (workspace/chrome.jsx:258). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '9px 14px',
          background: 'var(--cd-bg-card-header)',
          borderBottom: '1px solid var(--cd-seam)',
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 10,
            color: 'var(--cd-fg-secondary)',
            letterSpacing: '0.16em',
            fontWeight: 500,
            textTransform: 'uppercase',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 8, color: 'var(--cd-sig-iris)' }}>
            ✦
          </span>
          Suggestions
        </span>
        {suggestions.length > 0 && (
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10,
              color: 'var(--cd-fg-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {suggestions.length}
          </span>
        )}
      </div>

      <div style={{ overflowY: 'auto', minHeight: 0, background: 'var(--cd-bg-inset)' }}>
        {isError ? (
          <p style={mutedStyle}>Suggestions unavailable.</p>
        ) : isLoading && suggestions.length === 0 ? (
          <p style={mutedStyle}>Loading…</p>
        ) : suggestions.length === 0 ? (
          <p style={mutedStyle}>No active suggestions.</p>
        ) : (
          suggestions.map((s) => (
            <SuggestionCard key={s.suggestion_id} suggestion={s} promoting={false} />
          ))
        )}
      </div>
    </div>
  );
}
